import React, { useState, useMemo, useEffect } from 'react';
import Select from 'react-select';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createTrail as createTrailService } from '../../services/trails';
import { uploadFile } from '../../services/files';
import checkEmptyString from '../../helpers/check-empty-string';
import SelectCourses from './select-courses.js';
import { ArrowUp, ArrowDown } from 'react-feather';
import { hideModal, showModal } from '../modal';
import SelectImage from '../select-image';
import { getSegmentationTypes } from '../../services/segmentations';

const Tabset = () => {
  const history = useHistory();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [thumbnail, setThumbnail] = useState('');
  const [segTypes, setSegTypes] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedRegistrations, setSelectedRegistrations] = useState([]);

  const selectImage = () => {
    showModal(
      'Cortar Imagem',
      <SelectImage aspect={1.712} onCutImage={onCutImage} />,
    );
  };

  const onCutImage = file => {
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => setThumbnail(`${reader.result}`);

      hideModal();
    }
  };

  function dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const createTrail = async event => {
    event.preventDefault();

    const proceedWithCreation = async () => {
      try {
        if (!selectedCourses || !selectedCourses.length) {
          throw new Error('Selecione ao menos um curso para continuar!');
        }

        if (checkEmptyString(name)) {
          throw new Error('Informe um nome para a disciplina!');
        }

        if (checkEmptyString(description)) {
          throw new Error('Informe uma descrição para a disciplina!');
        }

        const localCourses = selectedCourses.map((selectedCourse, index) => ({
          position: index + 1,
          course_id: selectedCourse.id,
        }));

        const newTrail = {
          name: name,
          description: description,
          courses: localCourses,
          thumbnail: undefined,
        };

        const genericType = segTypes.find(item => item.type === 'Generic');
        const roleFiltered =  genericType.items.filter(seg => selectedRoles.some(role => seg.item === role.label));
        const registrationsFiltered =  genericType.items.filter(seg => selectedRegistrations.some(reg => seg.item === reg.label));

        if (
          (selectedRoles && selectedRoles.length) ||
          (selectedRegistrations && selectedRegistrations.length)
        ) {
          newTrail.segmentation_items = [
            ...(roleFiltered || []).map(role => ({
              segmentation_item_id: role.segmentation_item_id,
            })),
            ...(registrationsFiltered || []).map(reg => ({
              segmentation_item_id: reg.segmentation_item_id,
            })),
          ];
        }

        if (thumbnail && thumbnail.length && thumbnail.includes('base64')) {
          try {
            const localThumbnail = dataURLtoFile(thumbnail, `thumbnail.png`);

            const formData = new FormData();
            formData.append('file', localThumbnail);

            const { reference } = await uploadFile(formData);
            newTrail.thumbnail = reference;
          } catch (e) {
            throw new Error('Erro ao mudar a imagem de capa. ' + e.message);
          }
        }

        await createTrailService(newTrail);

        toast.success('Disciplina criada com sucesso');

        history.push(
          `${process.env.PUBLIC_URL}/disciplinas/listar-disciplinas`,
        );
      } catch (e) {
        const errorMessage = e.response || e.message;
        toast.error('Erro ao cadastrar disciplina.' + errorMessage);
      }
    };

    await proceedWithCreation();
  };

  const changeCoursePosition = async (course, up) => {
    const foundCourse = selectedCourses.find(c => c.id === course.id);
    if (foundCourse) {
      const indexOfFoundCourse = selectedCourses.indexOf(foundCourse);
      if (
        indexOfFoundCourse > -1 &&
        indexOfFoundCourse < selectedCourses.length
      ) {
        if (up) {
          const previousCourse = selectedCourses[indexOfFoundCourse - 1];
          selectedCourses.splice(indexOfFoundCourse - 1, 1, foundCourse);
          selectedCourses.splice(indexOfFoundCourse, 1, previousCourse);
        } else {
          const nextCourse = selectedCourses[indexOfFoundCourse + 1];
          selectedCourses.splice(indexOfFoundCourse + 1, 1, foundCourse);
          selectedCourses.splice(indexOfFoundCourse, 1, nextCourse);
        }
      }
    }

    setSelectedCourses([...selectedCourses]);
  };

  const checkShouldShowChangePositionButton = (course, up) => {
    const foundCourse = selectedCourses.find(c => c.id === course.id);
    if (foundCourse) {
      const indexOfFoundCourse = selectedCourses.indexOf(foundCourse);
      if (
        indexOfFoundCourse > -1 &&
        indexOfFoundCourse < selectedCourses.length
      ) {
        if (up) {
          return indexOfFoundCourse > 0;
        } else {
          return indexOfFoundCourse < selectedCourses.length - 1;
        }
      }
    }

    return false;
  };

  const selectCourses = () => {
    showModal(
      'Selecionar Módulos',
      <SelectCourses
        initialSelectedCourses={[...selectedCourses]}
        onSelectCourses={selectedCourses => {
          setSelectedCourses([...selectedCourses]);
          hideModal();
        }}
      />,
    );
  };

  useEffect(() => {
    (async () => {
      const localSegTypes = await getSegmentationTypes();
      setSegTypes(localSegTypes);
    })();
  }, []);

  const roles = useMemo(() => {
    if (segTypes && segTypes.length) {
      const roleSegType = segTypes.find(segType => segType.type === 'Cargo');
      if (roleSegType) {
        if (roleSegType.items && roleSegType.items.length) {
          return roleSegType.items.map(item => ({
            label: item.item,
            value: item.segmentation_item_id,
          }));
        }
      }
    }

    return [];
  }, [segTypes]);

  const registrations = useMemo(() => {
    if (segTypes && segTypes.length) {
      const registrationType = segTypes.find(
        segType => segType.type === 'Matricula',
      );
      if (registrationType) {
        if (registrationType.items && registrationType.items.length) {
          return registrationType.items.map(item => ({
            label: item.item,
            value: item.segmentation_item_id,
          }));
        }
      }
    }

    return [];
  }, [segTypes]);

  return (
    <>
      <div className="tab-pane fade active show">
        <form className="needs-validation" onSubmit={e => createTrail(e)}>
          <div className="row">
            <div className="col-sm-12">
              <div className="form-group row">
                <label className="col-xl-2 col-md-4">
                  <span>*</span> Nome
                </label>
                <input
                  className="form-control col-md-10"
                  id="validationCustom0"
                  type="text"
                  required={true}
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
              <div className="form-group row">
                <label className="col-xl-2 col-md-4">
                  <span>*</span> Descrição
                </label>
                <textarea
                  className="form-control col-md-10"
                  id="validationCustom1"
                  type="text"
                  rows="3"
                  required={true}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
              </div>

              <div className="form-group row">
                <label className="col-xl-2 col-md-4">
                  <span>*</span> Cursos
                </label>

                <div className="border rounded col-xl-10 col-md-8 p-3 text-center">
                  <button
                    className="btn btn-primary mb-3"
                    type="button"
                    onClick={selectCourses}
                  >
                    Selecionar Cursos
                  </button>

                  <div className="row">
                    {selectedCourses && selectedCourses.length ? (
                      selectedCourses.map(course => (
                        <div key={course.id} className="col-sm-12">
                          <div
                            className="card border"
                            style={{ marginBottom: '10px' }}
                          >
                            <div
                              className="card-body"
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '20px',
                              }}
                            >
                              <h4 className="card-title">{course.title}</h4>
                              <div>
                                <div className="m-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      checkShouldShowChangePositionButton(
                                        course,
                                        true,
                                      ) && changeCoursePosition(course, true)
                                    }
                                    className="btn btn-xs btn-light btn-small ml-2"
                                    disabled={
                                      !checkShouldShowChangePositionButton(
                                        course,
                                        true,
                                      )
                                    }
                                  >
                                    <ArrowUp size={22} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      checkShouldShowChangePositionButton(
                                        course,
                                        false,
                                      ) && changeCoursePosition(course, false)
                                    }
                                    className="btn btn-xs btn-light btn-small ml-2"
                                    disabled={
                                      !checkShouldShowChangePositionButton(
                                        course,
                                        false,
                                      )
                                    }
                                  >
                                    <ArrowDown size={22} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group row mb-2">
                <label className="col-xl-2 col-md-4">Imagem</label>
                <div className="text-center col-md-10 border rounded p-1">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={selectImage}
                  >
                    Selecionar Imagem
                  </button>

                  <div>
                    {thumbnail ? (
                      <img
                        className="mt-1"
                        style={{ width: '400px' }}
                        src={thumbnail}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group row mb-2">
                <label className="col-xl-2 col-md-4" htmlFor="roles">
                  Cargo
                </label>
                <div className="text-center col-md-10 border rounded p-1">
                  <Select
                    id="roles"
                    isMulti={true}
                    options={roles}
                    value={selectedRoles}
                    onChange={options => setSelectedRoles(options)}
                  />
                </div>
              </div>

              <div className="form-group row mb-2">
                <label className="col-xl-2 col-md-4" htmlFor="registrations">
                  Matricula
                </label>
                <div className="text-center col-md-10 border rounded p-1">
                  <Select
                    id="registrations"
                    isMulti={true}
                    options={registrations}
                    value={selectedRegistrations}
                    onChange={options => setSelectedRegistrations(options)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pull-right">
            <button
              type="button"
              className="btn btn-secondary mr-2"
              onClick={() => {
                history.push(
                  `${process.env.PUBLIC_URL}/disciplinas/listar-disciplinas`,
                );
              }}
            >
              Cancelar
            </button>

            <button type="submit" className="btn btn-primary">
              Salvar
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Tabset;
